import type { Embodiment, Tip } from "@/api";

export type EmbodimentEditDraft = {
  loadedUuid: string | null;
  endEffectorUuid: string | null;
  name: string;
  tip: Tip | null;
};

function createBlankDraft(tip: Tip | null): EmbodimentEditDraft {
  return {
    loadedUuid: null,
    endEffectorUuid: null,
    name: "",
    tip,
  };
}

function createDraftFromEmbodiment(embodiment: Embodiment, tip: Tip | null): EmbodimentEditDraft {
  return {
    loadedUuid: embodiment.uuid,
    endEffectorUuid: embodiment.end_effector?.uuid ?? null,
    name: embodiment.name,
    tip,
  };
}

function normalizeDraft(draft: EmbodimentEditDraft) {
  return {
    loadedUuid: draft.loadedUuid,
    endEffectorUuid: draft.endEffectorUuid,
    name: draft.name,
    tipUuid: draft.tip?.uuid ?? null,
  };
}

export class EmbodimentEditController {
  readonly baseline: EmbodimentEditDraft;
  readonly draft: EmbodimentEditDraft;

  private constructor(baseline: EmbodimentEditDraft, draft: EmbodimentEditDraft) {
    this.baseline = baseline;
    this.draft = draft;
  }

  static blank(tip: Tip | null): EmbodimentEditController {
    const draft = createBlankDraft(tip);
    return new EmbodimentEditController({ ...draft }, draft);
  }

  static fromEmbodiment(embodiment: Embodiment, tip: Tip | null): EmbodimentEditController {
    const draft = createDraftFromEmbodiment(embodiment, tip);
    return new EmbodimentEditController({ ...draft }, draft);
  }

  // 기존 embodiment 정보를 바탕으로 '새로 생성'하는 초안이다. 필드는 그대로 채우고 loadedUuid 는
  // 비운다. 그래서 저장하면 POST 로 보내고 원본은 바뀌지 않는다.
  static cloneFrom(embodiment: Embodiment, tip: Tip | null): EmbodimentEditController {
    const base = createDraftFromEmbodiment(embodiment, tip);
    const draft: EmbodimentEditDraft = { ...base, loadedUuid: null };
    return new EmbodimentEditController({ ...draft }, draft);
  }

  get dirty(): boolean {
    return (
      JSON.stringify(normalizeDraft(this.baseline)) !== JSON.stringify(normalizeDraft(this.draft))
    );
  }

  patch(patch: Partial<EmbodimentEditDraft>): EmbodimentEditController {
    return new EmbodimentEditController(this.baseline, { ...this.draft, ...patch });
  }
}
